import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip
} from 'recharts'
import { Header } from 'semantic-ui-react'
import styled from 'styled-components'
import { CoolFarmCropResultsType } from '~/api/services/coolFarmCrop'
import { formatNumber } from '~/utils/helpers'

interface EmissionBreakdownProps {
  results: CoolFarmCropResultsType
}

const Segment = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`

const safeParseFloat = (value: string | number | undefined): number => {
  if (value === undefined || value === null) return 0
  return parseFloat(String(value))
}

const formatChartNumber = (num: number) => {
  if (num >= 1000000) {
    return formatNumber((num / 1000000), 2) + 'M'
  } else if (num >= 1000) {
    return formatNumber((num / 1000), 2) + 'k'
  }
  return formatNumber(num, 2)
}

const formatTooltipValue = (value: number) => {
  return formatChartNumber(value)
}

const EmissionBreakdown: React.FC<EmissionBreakdownProps> = ({ results }) => {
  const { t } = useTranslation()

  const pieData = useMemo(() => {
    const total = safeParseFloat(results.totalEmission)
    if (total === 0) return []
    
    return [
      { name: t('Fertiliser production'), value: safeParseFloat(results.fertiliserProductionTotalEmission), color: '#8884d8' },
      { name: t('Soil / fertiliser'), value: safeParseFloat(results.fertiliserSoilTotalEmission), color: '#82ca9d' },
      { name: t('Crop protection'), value: safeParseFloat(results.pesticideTotalEmission), color: '#ffc658' },
      { name: t('Residue management'), value: safeParseFloat(results.residueTotalEmission), color: '#ff7300' },
      { name: t('Management changes'), value: safeParseFloat(results.managementChangesTotalEmission), color: '#00ff00' },
      { name: t('Field energy'), value: safeParseFloat(results.fieldEnergyTotalEmission), color: '#0088fe' },
      { name: t('Machinery'), value: safeParseFloat(results.machineryTotalEmission), color: '#ff0000' },
      { name: t('Waste water'), value: safeParseFloat(results.wasteWaterTotalEmission), color: '#00ffff' },
      { name: t('Transportation'), value: safeParseFloat(results.transportationTotalEmission), color: '#ff00ff' },
      { name: t('Irrigation'), value: safeParseFloat(results.irrigationTotalEmission), color: '#ffff00' }
    ].filter(item => item.value > 0)
  }, [results, t])

  return (
    <Segment>
      <Header as='h3' textAlign='center' style={{ marginBottom: '20px' }}>
        {t('Emission Breakdown')}
      </Header>
      <div style={{ display: 'flex', gap: '40px', alignItems: 'flex-start' }}>
        <div style={{ flex: '1', minWidth: '300px' }}>
          {pieData
            .sort((a, b) => b.value - a.value)
            .map((item, index) => {
              const percentage = (item.value / safeParseFloat(results.totalEmission)) * 100
              return (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: item.color,
                        borderRadius: '2px'
                      }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                      {item.name}
                    </span>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                    {formatChartNumber(item.value)} ({formatNumber(percentage, 2)}%)
                  </div>
                </div>
              )
            })}
        </div>
        
        <div style={{ flex: '1', minWidth: '300px' }}>
          <ResponsiveContainer width='100%' height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx='50%'
                cy='50%'
                labelLine={false}
                label={false}
                outerRadius={120}
                fill='#8884d8'
                dataKey='value'
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={formatTooltipValue} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Segment>
  )
}

export default EmissionBreakdown
